'use client';

import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MdPhone } from 'react-icons/md';
import { ArrowRight, Check } from 'lucide-react';
import BookDemoModal from '@/components/ui/BookDemoModal';
import CalendlyModal from '@/components/ui/CalendlyModal';
import Magnetic from '@/components/motion/Magnetic';
import SplitReveal from '@/components/motion/SplitReveal';

// One lens ring per 44px cell; the lit copy is the same grid, filled.
const RINGS =
  'radial-gradient(circle at center, transparent 8px, rgba(255,255,255,0.09) 9px, rgba(255,255,255,0.09) 10px, transparent 11px)';
const RINGS_LIT =
  'radial-gradient(circle at center, rgba(1,167,255,0.35) 0, rgba(1,167,255,0.35) 8px, rgba(110,231,183,0.95) 9px, rgba(110,231,183,0.95) 10px, transparent 11px)';
const LENS_MASK = 'radial-gradient(240px circle at var(--mx) var(--my), #000 0%, transparent 70%)';

/**
 * The end of the story the hero starts: order #4821 has been delivered. The
 * field behind the copy is rows of lenses that come into focus wherever the
 * cursor goes — a masked, lit copy of the same ring pattern, so it costs two
 * custom properties per pointer move.
 */
const CtaSection = () => {
  const block = useRef(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  const track = () => {
    if (typeof window !== 'undefined' && window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }
  };
  const openDemo = () => {
    track();
    setDemoOpen(true);
  };
  const openCalendly = () => {
    track();
    setCalendlyOpen(true);
  };

  const onPointerMove = (e) => {
    const el = block.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <>
      <section aria-label="Book a demo" className="px-5 pb-4 pt-12">
        <motion.div
          ref={block}
          onPointerMove={onPointerMove}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ '--mx': '-999px', '--my': '-999px' }}
          className="container relative mx-auto overflow-hidden rounded-[2rem] bg-ink px-6 py-20 text-center sm:py-32"
        >
          {/* glow — brand blue + mint, no purple */}
          <div className="pointer-events-none absolute -left-16 -top-10 size-72 rounded-full bg-primary-blue/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -right-16 size-72 rounded-full bg-emerald-400/15 blur-3xl" />

          {/* lens field */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: RINGS, backgroundSize: '44px 44px' }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 motion-reduce:hidden"
            style={{
              backgroundImage: RINGS_LIT,
              backgroundSize: '44px 44px',
              WebkitMaskImage: LENS_MASK,
              maskImage: LENS_MASK,
            }}
          />

          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white ring-1 ring-white/15 backdrop-blur">
              <span className="grid size-4 place-items-center rounded-full bg-emerald-400 text-secondary-dark">
                <Check className="size-3" strokeWidth={3.5} />
              </span>
              Order #4821 · Delivered
            </span>

            <SplitReveal
              as="h2"
              className="display mx-auto mt-7 max-w-4xl text-[clamp(36px,5.4vw,84px)] text-white"
            >
              Every order, start to finish, on one platform.
            </SplitReveal>
            <p className="mx-auto mt-5 max-w-xl text-lg text-white/60">
              Ready to experience top-notch service? Contact us today and join our satisfied
              customers!
            </p>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Magnetic>
                <button
                  type="button"
                  onClick={openDemo}
                  className="group inline-flex items-center gap-2 rounded-full bg-primary-blue px-7 py-3.5 font-semibold text-white shadow-[0_12px_30px_-8px_rgba(1,167,255,0.7)] transition-colors hover:bg-[#0095e6] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-dark"
                >
                  Book a demo
                  <ArrowRight
                    className="size-4 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2.5}
                  />
                </button>
              </Magnetic>
              <Magnetic>
                <button
                  type="button"
                  onClick={openCalendly}
                  className="group inline-flex items-center gap-3 rounded-full bg-white py-2 pl-2 pr-6 font-semibold text-secondary-dark shadow-lg transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-dark"
                >
                  <span className="grid size-10 place-items-center rounded-full bg-primary-blue transition-transform group-hover:scale-105">
                    <MdPhone size={20} color="#fff" />
                  </span>
                  Book a call
                </button>
              </Magnetic>
            </div>
          </div>
        </motion.div>
      </section>

      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
      <CalendlyModal isOpen={calendlyOpen} onClose={() => setCalendlyOpen(false)} />
    </>
  );
};

export default CtaSection;
